// shared/totals.ts
// THE rule for what a day adds up to. Both sides of the wire use it: the
// server builds `TodayResponse.consumed` from here, and the client's optimistic
// layer recomputes from here after a local insert or removal.
//
// It lives in shared/ for the same reason getDailyBudget does — the moment the
// client keeps its own copy of the arithmetic, the number on screen and the
// number in the database start to disagree, and in a calorie tracker that is
// not a cosmetic bug.
import type { Drink, FoodEntry, MacroTotals } from "./types";
import { mergeMicros } from "./micros";

/** Food macros plus every drink's calories — what the ring counts as eaten. */
export function sumConsumed(
  entries: Pick<FoodEntry, "calories" | "protein_g" | "carbs_g" | "fat_g" | "sugar_g" | "fiber_g" | "micros">[],
  drinks: Pick<Drink, "kcal">[],
): MacroTotals {
  const food = entries.reduce(
    (a, e) => ({
      calories: a.calories + e.calories,
      protein_g: a.protein_g + (e.protein_g ?? 0),
      carbs_g:   a.carbs_g   + (e.carbs_g   ?? 0),
      fat_g:     a.fat_g     + (e.fat_g     ?? 0),
      sugar_g:   a.sugar_g   + (e.sugar_g   ?? 0),
      fiber_g:   a.fiber_g   + (e.fiber_g   ?? 0),
    }),
    { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, sugar_g: 0, fiber_g: 0 },
  );
  return {
    ...food,
    calories: food.calories + drinks.reduce((s, d) => s + d.kcal, 0),
    micros: mergeMicros(entries.map((e) => e.micros)),
  };
}

/** Water only. Coffee and tea carry calories but do not count as hydration. */
export function sumWaterMl(drinks: Pick<Drink, "type" | "volume_ml">[]): number {
  return drinks.reduce((s, d) => s + (d.type === "water" ? d.volume_ml : 0), 0);
}
